import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, FloppyDisk, Eye,
} from "@phosphor-icons/react";
import {
  useFieldsBuilder, FieldsDndProvider, FieldsPalette, FieldsCanvas, RoutingRulesEditor,
} from "@/components/fieldsBuilder";

export default function FormBuilder() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    title: "",
    description: "",
    whatsapp_number: user?.whatsapp_number || "",
    intro_message: "Halo, saya ingin mengisi formulir berikut:",
    success_message: "Terima kasih! Pesan akan terkirim ke WhatsApp.",
    fields: [],
    routing_rules: [],
    sheets_webhook_url: "",
    active: true,
    slug: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const fb = useFieldsBuilder(form, setForm);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/forms/${id}`)
      .then(r => setForm(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Judul wajib diisi");
    if (!form.whatsapp_number.trim()) return toast.error("Nomor WhatsApp wajib diisi");
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        whatsapp_number: form.whatsapp_number,
        intro_message: form.intro_message,
        success_message: form.success_message,
        fields: form.fields,
        routing_rules: form.routing_rules || [],
        sheets_webhook_url: form.sheets_webhook_url || "",
      };
      if (isEdit) {
        payload.active = form.active;
        const { data } = await api.patch(`/forms/${id}`, payload);
        setForm(data);
        toast.success("Tersimpan!");
      } else {
        const { data } = await api.post(`/forms`, payload);
        toast.success("Formulir dibuat!");
        nav(`/forms/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FDFBF7]"><Navbar /><p className="p-12 font-display text-2xl">Memuat…</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <Link to="/dashboard" className="inline-flex items-center gap-2 font-bold" data-testid="form-back-link">
            <ArrowLeft size={18} weight="bold" /> Kembali
          </Link>
          <div className="flex gap-2 flex-wrap">
            {isEdit && form.slug && (
              <a href={`/f/${form.slug}`} target="_blank" rel="noreferrer" className="nb-btn" data-testid="form-preview-link">
                <Eye size={18} weight="bold" /> Pratinjau
              </a>
            )}
            <button onClick={save} disabled={saving} className="nb-btn nb-btn-primary" data-testid="form-save-button">
              <FloppyDisk size={18} weight="bold" /> {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </div>

        <div className="mt-6">
          <span className="nb-badge">{isEdit ? "Edit Formulir" : "Formulir Baru"}</span>
          <h1 className="font-display font-black text-4xl tracking-tight mt-3">{isEdit ? form.title : "Buat formulir baru"}</h1>
          <p className="text-[#4A4A4A] mt-1 text-sm">Seret elemen dari palet ke kanvas. Atur urutan & properti per field.</p>
        </div>

        <FieldsDndProvider onDragEnd={fb.handleDragEnd}>
          <div className="grid lg:grid-cols-12 gap-6 mt-8">
            <div className="lg:col-span-3 space-y-4">
              <SettingsPanel form={form} setForm={setForm} isEdit={isEdit} />
            </div>

            <div className="lg:col-span-3">
              <FieldsPalette onAdd={fb.addFieldByType} />
            </div>

            <div className="lg:col-span-6 space-y-4">
              <FieldsCanvas
                fields={fb.fields}
                updateField={fb.updateField}
                removeField={fb.removeField}
                moveField={fb.moveField}
              />

              <RoutingRulesEditor
                fields={fb.fields}
                rules={fb.routing_rules}
                onChange={fb.setRoutingRules}
              />

              <SheetsWebhookEditor
                webhookUrl={form.sheets_webhook_url || ""}
                onChange={(url) => setForm({ ...form, sheets_webhook_url: url })}
              />
            </div>
          </div>
        </FieldsDndProvider>
      </main>
    </div>
  );
}

function SettingsPanel({ form, setForm, isEdit }) {
  return (
    <div className="nb-card p-5 space-y-3">
      <h2 className="font-display font-bold text-lg">Pengaturan</h2>
      <div>
        <label className="nb-label">Judul Formulir</label>
        <input className="nb-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Misal: Reservasi Salon" data-testid="form-title-input" />
      </div>
      <div>
        <label className="nb-label">Deskripsi</label>
        <textarea className="nb-input" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="form-desc-input" />
      </div>
      <div>
        <label className="nb-label">No. WA Tujuan</label>
        <input className="nb-input" value={form.whatsapp_number} onChange={e => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="628..." data-testid="form-wa-input" />
      </div>
      <div>
        <label className="nb-label">Pesan Pembuka WA</label>
        <textarea className="nb-input" rows={2} value={form.intro_message} onChange={e => setForm({ ...form, intro_message: e.target.value })} data-testid="form-intro-input" />
      </div>
      <div>
        <label className="nb-label">Pesan Setelah Submit</label>
        <input className="nb-input" value={form.success_message} onChange={e => setForm({ ...form, success_message: e.target.value })} data-testid="form-success-input" />
      </div>
      {isEdit && (
        <div className="flex items-center gap-3">
          <input type="checkbox" id="active" checked={!!form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="w-5 h-5 border-2 border-[#121212]" data-testid="form-active-toggle" />
          <label htmlFor="active" className="font-bold text-sm">Formulir aktif</label>
        </div>
      )}
    </div>
  );
}

const FORM_SCRIPT = `function doPost(e) {
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  const data = JSON.parse(e.postData.contents);
  const answers = data.answers || {};
  const headers = ['Waktu', 'Form', 'No. WA Tujuan', 'Label Tim', ...Object.keys(answers)];
  if (sheet.getLastRow() === 0) sheet.appendRow(headers);
  const row = [data.submitted_at, data.form_title, data.routed_whatsapp, data.routed_label];
  Object.keys(answers).forEach(k => row.push(answers[k]));
  sheet.appendRow(row);
  return ContentService.createTextOutput(JSON.stringify({ok:true})).setMimeType(ContentService.MimeType.JSON);
}`;

function SheetsWebhookEditor({ webhookUrl, onChange }) {
  const [showHelp, setShowHelp] = useState(false);
  return (
    <div className="nb-card p-5 bg-[#A7E4C0]/30" data-testid="sheets-webhook-card">
      <span className="nb-badge bg-[#A7E4C0]">Google Sheets</span>
      <h3 className="font-display font-bold text-lg mt-2">Kirim otomatis ke Spreadsheet</h3>
      <p className="text-xs text-[#4A4A4A] mt-1">Setiap submission akan otomatis tercatat ke Google Sheet Anda.</p>
      <div className="mt-3">
        <label className="nb-label">URL Web App Apps Script</label>
        <input className="nb-input" value={webhookUrl} onChange={e => onChange(e.target.value)} placeholder="https://script.google.com/macros/s/.../exec" data-testid="sheets-webhook-url-input" />
      </div>
      <button onClick={() => setShowHelp(s => !s)} className="nb-btn mt-3" data-testid="sheets-help-toggle">
        {showHelp ? "Sembunyikan Panduan" : "Lihat Panduan Setup (1 menit)"}
      </button>
      {showHelp && (
        <div className="mt-3 space-y-2 text-sm">
          <ol className="list-decimal pl-5 space-y-1">
            <li>Buka Google Sheets baru. Menu <strong>Extensions → Apps Script</strong>.</li>
            <li>Hapus kode default, salin & tempel kode di bawah.</li>
            <li>Klik <strong>Deploy → New deployment → Web app</strong>. Set akses ke <strong>Anyone</strong>.</li>
            <li>Copy URL Web App, tempel ke kolom di atas. Klik Simpan.</li>
          </ol>
          <pre className="mono text-xs bg-[#121212] text-[#FDFBF7] p-3 rounded-lg overflow-x-auto border-2 border-[#121212]" data-testid="sheets-script-code">{FORM_SCRIPT}</pre>
          <button onClick={() => { navigator.clipboard.writeText(FORM_SCRIPT); toast.success("Script disalin"); }} className="nb-btn" data-testid="sheets-copy-script-button">
            Salin Script
          </button>
        </div>
      )}
    </div>
  );
}
